import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/site/Layout";
import Home from "@/pages/Home";
import Romania from "@/pages/Romania";
import Admin from "@/pages/Admin";
import ThankYou from "@/pages/ThankYou";
import { SeoPage, ProgramsPage, FaqPage, ContactPage, AboutPage, PAGES } from "@/pages/SeoPages";
import { InsightsPage, ArticlePage } from "@/pages/Insights";
import {
  DestinationsPage, CountryPage, CoursesPage, CoursePage, UniversitiesPage, UniversityPage,
  FindYourPath, ServicesPage, ScholarshipsPage, VisaHubPage, ChecklistPage, UpdatesPage, EventsPage,
  TestPrepPage, CostCalculatorPage, IntakePlannerPage, FundingPage, StudentStoriesPage, StudyAbroadPage, LegalPage,
} from "@/pages/Platform";

function ScrollManager() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
          else el.scrollIntoView();
        }
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/study-in-romania" element={<Romania />} />
            <Route path="/thank-you" element={<ThankYou />} />
            {Object.values(PAGES).map((p) => (
              <Route key={p.path} path={p.path} element={<SeoPage config={p} />} />
            ))}
            <Route path="/programs" element={<ProgramsPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<ArticlePage />} />
            <Route path="/blog" element={<InsightsPage />} />
            <Route path="/blog/:slug" element={<ArticlePage />} />
            <Route path="/destinations" element={<DestinationsPage />} />
            <Route path="/study-in-uk" element={<CountryPage cslug="uk" />} />
            <Route path="/study-in-germany" element={<CountryPage cslug="germany" />} />
            <Route path="/study-in-italy" element={<CountryPage cslug="italy" />} />
            <Route path="/study-in-france" element={<CountryPage cslug="france" />} />
            <Route path="/study-in-ireland" element={<CountryPage cslug="ireland" />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:slug" element={<CoursePage />} />
            <Route path="/universities" element={<UniversitiesPage />} />
            <Route path="/universities/:slug" element={<UniversityPage />} />
            <Route path="/find-your-path" element={<FindYourPath />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/scholarships" element={<ScholarshipsPage />} />
            <Route path="/visa-guidance" element={<VisaHubPage />} />
            <Route path="/checklist" element={<ChecklistPage />} />
            <Route path="/updates" element={<UpdatesPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/study-abroad" element={<StudyAbroadPage />} />
            <Route path="/study-in-usa" element={<CountryPage cslug="usa" />} />
            <Route path="/study-in-canada" element={<CountryPage cslug="canada" />} />
            <Route path="/study-in-australia" element={<CountryPage cslug="australia" />} />
            <Route path="/study-in-new-zealand" element={<CountryPage cslug="new-zealand" />} />
            <Route path="/study-in-netherlands" element={<CountryPage cslug="netherlands" />} />
            <Route path="/study-in-switzerland" element={<CountryPage cslug="switzerland" />} />
            <Route path="/study-in-spain" element={<CountryPage cslug="spain" />} />
            <Route path="/study-in-uae" element={<CountryPage cslug="uae" />} />
            <Route path="/study-in-singapore" element={<CountryPage cslug="singapore" />} />
            <Route path="/study-in-malaysia" element={<CountryPage cslug="malaysia" />} />
            <Route path="/study-in-mauritius" element={<CountryPage cslug="mauritius" />} />
            <Route path="/test-preparation" element={<TestPrepPage />} />
            <Route path="/cost-calculator" element={<CostCalculatorPage />} />
            <Route path="/intake-planner" element={<IntakePlannerPage />} />
            <Route path="/education-loans" element={<FundingPage />} />
            <Route path="/student-stories" element={<StudentStoriesPage />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/cookies" element={<LegalPage type="cookies" />} />
            <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
          </Route>
          <Route path="/admin" element={<Admin />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
